import { inject, Injectable, InjectionToken, ValueProvider } from '@angular/core';
import { EntityType } from '@panaya/ai-chat/shared';
import { AnyCanvasEntityMapping, CanvasEntityMapping } from './canvas-entity-mapping.model';

interface CanvasEntityMappingRegistration {
    entityType: EntityType;
    mapping: AnyCanvasEntityMapping;
}

const CANVAS_ENTITY_MAPPINGS = new InjectionToken<CanvasEntityMappingRegistration[]>('__CANVAS_ENTITY_MAPPINGS__', {
    factory: (): CanvasEntityMappingRegistration[] => []
});

/**
 * Registers how the canvas draws one entity type's data. A host registers one mapping per entity type its
 * agents return; a type with no mapping is drawn by the fallback walk. Give the data type, as in
 * `provideCanvasEntityMapping<YourData>(…)` or a `CanvasEntityMapping<YourData>` constant, so its paths are checked. A later registration for a type replaces an
 * earlier one.
 */
export function provideCanvasEntityMapping<TData>(entityType: EntityType, mapping: CanvasEntityMapping<TData>): ValueProvider {
    // A typed mapping's paths are checked where the host writes it; the engine reads any mapping as plain strings.
    const registration: CanvasEntityMappingRegistration = { entityType, mapping };

    return { provide: CANVAS_ENTITY_MAPPINGS, useValue: registration, multi: true };
}

@Injectable({ providedIn: 'root' })
export class CanvasEntityMappingRegistry {
    private readonly mappings = new Map<EntityType, AnyCanvasEntityMapping>(
        inject(CANVAS_ENTITY_MAPPINGS).map(({ entityType, mapping }) => [entityType, mapping])
    );

    public find(entityType: EntityType): AnyCanvasEntityMapping | null {
        return this.mappings.get(entityType) ?? null;
    }
}
